import React from "react";
import axios from "axios";
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import ViewModuleIcon from "@mui/icons-material/ViewModule";   // icône tableau
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";   // icône cards
import Tooltip from "@mui/material/Tooltip";
import { normalizePhoneNumber } from "../utils/normalizePhoneNumber.js";

import SondageCardsView from "./SondageCardsView";
import { useLanguage } from '../context/LanguageContext.js';
import { logDev } from "../utils/logger.js";

const TableauSondage = ({ contacts, columns, processRowUpdate, periodeId, isSurveyClosed, isAdmin, totalVotes, hasVotes, nombrePeriodes, groupeActif, onContactCreated }) => {
  const apiRef = useGridApiRef(); // ✅ création de la ref
  const { t } = useLanguage();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [search, setSearch] = React.useState("");
  const [participationOuverte, setParticipationOuverte] = React.useState(false);
  const [nomParticipant, setNomParticipant] = React.useState("");
  const [mailParticipant, setMailParticipant] = React.useState("");
  const [telephoneParticipant, setTelephoneParticipant] = React.useState("");
  const [combienParticipant, setCombienParticipant] = React.useState(1);
  const [erreurParticipation, setErreurParticipation] = React.useState("");

  // 👇 Ajoute ce log pour voir ce que reçoit ton DataGrid
  logDev("[TableauSondage] rows envoyés au DataGrid", contacts);
  logDev("[TableauSondage] totalVotes reçu", totalVotes);

  const [vueCards, setVueCards] = React.useState(true);

  const filteredContacts = contacts.filter(c =>
    c.nom?.toLowerCase().includes(search.toLowerCase())
  );

  const SearchBar = (
    <Box sx={{ mb: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <input
        type="text"
        placeholder={t("SearchContact")} 
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: isMobile ? "85%" : "180px",   
          padding: "8px 10px",
          fontSize: "15px",
          borderRadius: "6px",
          border: "1px solid #ccc"
        }}
      />
      
      {groupeActif?.lienWhatsApp && !isSurveyClosed && (
        <button
        type="button"
        onClick={() => setParticipationOuverte(true)}
        style={{
          marginTop: "10px",
          padding: "8px 14px",
          borderRadius: "6px",
          border: "1px solid #25D366",
          background: "white",
          color: "#128C7E",
          cursor: "pointer",
          fontSize: "14px"
        }}
      >
        {t("Participation")} 
      </button>
    )}
    </Box>
  );

  const ParticipationDialog = (

    <Dialog
      open={participationOuverte}
      onClose={() => setParticipationOuverte(false)}
      fullWidth
      maxWidth="sm"
    >
        
      <DialogTitle>{t("IParticipate")}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("EnterInformationToParticipateInSurvey")}
          </Typography>

          {erreurParticipation && (

            <Typography
              variant="body2"
              sx={{
                color: "error.main",
                fontWeight: "bold",
                mb: 2
              }}
            >
              {erreurParticipation}  
              </Typography>
          )}
          
          <TextField
          fullWidth
          required
          label={t("LastNameFirstName")}
          value={nomParticipant}
          onChange={(e) => setNomParticipant(e.target.value)}
          margin="normal"
          />

          <TextField
          fullWidth
          label={t("EmailOptional")}
          type="email"
          value={mailParticipant}
          onChange={(e) => setMailParticipant(e.target.value)}
          margin="normal"
          />

          <TextField
          fullWidth
          label={t("TelOptional")}
          value={telephoneParticipant}
          onChange={(e) => setTelephoneParticipant(e.target.value)}
          margin="normal"
          placeholder="+33612345678"
          />

          <TextField
          fullWidth
          required
          type="number"
          label={t("HowManyPeople")}
          value={combienParticipant}
          onFocus={() => setCombienParticipant("")}
          onChange={(e) => setCombienParticipant(e.target.value)}
          inputProps={{ min: 1, step: 1 }}
          margin="normal"
          />
      </DialogContent>

      <DialogActions>      
        <Button onClick={() => setParticipationOuverte(false)}>
          {t("Cancel")}
          </Button>

          <Button      
          variant="contained"
          onClick={async () => {
            const nom = nomParticipant.trim();          
            setErreurParticipation("");           
            if (!nom) {
              return;
              }
              try {
                await axios.post(
                  `${process.env.REACT_APP_API}/contacts`,
                  {
                    nom,
                    mail: mailParticipant.trim() || undefined,
                    telephone: telephoneParticipant.trim()
                    ? normalizePhoneNumber(telephoneParticipant.trim(), "FR")
                    : undefined,

                    groupe: groupeActif._id,
                    combien: Number(combienParticipant) || 0,
                    periodeId 
                  }
                );

                if (onContactCreated) {
                  await onContactCreated();
                  }

                  setNomParticipant("");
                  setMailParticipant("");
                  setTelephoneParticipant("");
                  setParticipationOuverte(false);
                  setCombienParticipant(1);

                  logDev("✅ Participant ajouté", {
                    groupeId: groupeActif._id,
                    nom
                    });

                    } catch (err) {
                      logDev("❌ Erreur ajout participant", {
                        message: err.message,
                        stack: err.stack
                      });

                      const status = err.response?.status;
                      const message = err.response?.data?.message;



                      if (status === 403) {
                        setErreurParticipation(
                          t("MaxParticipantsReached")
                          );
                          return;
                        }

                  setErreurParticipation(
                    message || t("ImpossibleAddParticipation") 
                    );
          }
        }}
        >        
          {t("Participate")}
        </Button>        
        </DialogActions>
        </Dialog>

        );


  // ---------------- MOBILE ----------------
  if (isMobile) {
    // 1. On enlève les lignes sans nom
    const cleanedContacts = filteredContacts.filter(c => c.nom);

    // 2. On vérifie si la ligne total existe déjà
    const hasTotal = cleanedContacts.some(c => c._id === "total-");

    // 3. On ajoute la ligne total seulement si elle n'existe pas
    const contactsWithTotal = hasTotal
      ? cleanedContacts
      : [...cleanedContacts, { _id: "total-", nom: t("Total"), [periodeId]: totalVotes }];

      return (
        
        <Box sx={{ width: "100%", px: 0, overflowX: "visible" }}>      
        {/* 🔍 Champ de recherche */}
        {SearchBar}
        {ParticipationDialog}

        {/* 🃏 Vue cartes (mobile) */}
        <SondageCardsView
        contacts={contactsWithTotal}
        periodeId={periodeId}
        isSurveyClosed={isSurveyClosed}
        isAdmin={isAdmin}
        onUpdate={(contact, periodeId, newValue) =>
          processRowUpdate({ ...contact, [periodeId]: newValue })
        }
        />
        </Box>
      );
    }

    const SondageViewToggleButton = ({ vueCards, setVueCards }) => (
      <Tooltip title={vueCards ? t("ShowTable") : t("ShowCards")} arrow>
        
        <button
          onClick={() => setVueCards(!vueCards)}
          style={{
            padding: "6px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            background: "#f5f5f5",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          
          {vueCards ? (
            <ViewModuleIcon style={{ fontSize: "22px" }} />
            ) : (
              <ViewAgendaIcon style={{ fontSize: "22px" }} />
              )}
              </button>
            </Tooltip>
            );

            // ---------------- DESKTOP ----------------
            return (
              <>
              {/*</><Box sx={{ px: 0 }}>*/}
              <Box
                sx={{
                  px: 0,
                  width: nombrePeriodes === 1 ? "100%" : "auto",
                  maxWidth: nombrePeriodes === 1 ? "100%" : "none",
                }}
              >


                {/* 🔍 Search + + bouton participation + Toggle */}
                <div               
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "10px",
                    marginBottom: "24px"
                  }}
                >
                  
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: "10px"
                    }}
                  >

                    {/* SearchBar à gauche */}
                    <input
                      type="text"
                      placeholder={t("SearchContact")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{
                        width: "200px",
                        padding: "6px 10px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        fontSize: "0.9rem"
                      }}
                    />

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                      }}
                    >

                    {groupeActif?.lienWhatsApp && !isSurveyClosed && (
                      <button
                        type="button"
                        onClick={() => setParticipationOuverte(true)}
                        style={{
                          padding: "8px 14px",
                          borderRadius: "6px",
                          border: "1px solid #25D366",
                          background: "white",
                          color: "#128C7E",
                          cursor: "pointer",
                          fontSize: "14px"
                        }}
                      >
                        {t("Participation")}                       
                      </button>
                    )}     

                    {/* Toggle à droite */}
                      <SondageViewToggleButton
                        vueCards={vueCards}
                        setVueCards={setVueCards}
                      />
                    </div>
                  </div>
                </div>

                {ParticipationDialog}

                {/* 🃏 MODE CARDS */}
                {vueCards && (
                  <SondageCardsView
                    contacts={filteredContacts}
                    periodeId={periodeId}
                    isSurveyClosed={isSurveyClosed}
                    isAdmin={isAdmin}
                    onUpdate={(contact, periodeId, newValue) =>
                      processRowUpdate({ ...contact, [periodeId]: newValue })
                    }
                  />
                )}

                  {/* 📊 MODE TABLEAU */}
                    {!vueCards && (
                      <Box sx={{ marginTop: 0 }}>
                        <DataGrid
                        apiRef={apiRef} // ✅ on la passe au DataGrid
                        key={contacts.length} // 🔄 force re-render
                        rows={filteredContacts}
                        columns={columns}
                        getRowId={(row) => row._id || row.id}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={(error) => {
                          logDev("[TableauSondage] erreur maj", error);
                        }}
                        experimentalFeatures={{ newEditingApi: true }}
                        editMode="cell"
                        disableSelectionOnClick
                        onCellClick={(params, event) => {
                          logDev("[TableauSondage] clic cellule", { id: params.id, field: params.field });
          
          // 👉 Ne rien faire si la colonne n’est pas éditable
          if (!params.isEditable) return;

          // sinon, on force l’édition dès le premier clic
          event.defaultMuiPrevented = true;
          const cellState = apiRef.current.getCellMode(params.id, params.field);

          if (cellState !== 'edit') {
            apiRef.current.startCellEditMode({ id: params.id, field: params.field });
            }
          }}        
          className="MuiDataGrid-root"
          autoHeight
          rowHeight={35}
          isCellEditable={(params) => {
            if (isSurveyClosed) return false; // ✅ désactive tout si sondage fermé           
            return params.row._id !== "total-";
          }}
          pagination
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 }, // 👈 taille par défaut
            },
          }}         
          pageSizeOptions={[5, 10, 25, 30]} // 👈 choix proposés à l’utilisateur
          sx={{
            /*height: 650, // ✅ hauteur fixe pour activer le scroll*/
            width: "100%",
            minWidth: 0,
            /*height: { xs: 400, sm: 650 },   // 📱 mobile = 400px*/
            backgroundColor: "#e4dcd2",
            /*overflowY: "auto",*/

            '& .MuiDataGrid-columnHeaders': {
              position: 'sticky',
              top: 0,
              backgroundColor: '#e4dcd2',
              zIndex: 1
            },

            "& .total-row": {
              fontWeight: "bold",
              backgroundColor: "#d9cbb8",
            },

            '& .MuiDataGrid-virtualScroller': {             
              overscrollBehavior: 'contain',
              paddingBottom: "0px !important",   // 🔥 enlève l’espace
            }
          }}
        />
      </Box>
      )}

      {/* Message CannotFixSurvey sous le tableau */}
      {isAdmin && !isSurveyClosed && !hasVotes && (
        <Typography
          variant="body2"
          sx={{ color: "#3399FF", mt: 1, fontStyle: "italic", textAlign: "center" }}
        >
        {t('CannotFixSurvey')}
        </Typography>
      )}

      <Box
        sx={{
          mt: 2,
          p: 2,
          backgroundColor: "#d9cbb8",
          fontWeight: "bold",
          borderRadius: 2,
          display: "flex",
          justifyContent: "center"
        }}
      >
        {t('TotalVotes')} {totalVotes}     
      </Box>
    </Box>
  </>
  );
};

export default TableauSondage;